/**
 * InboxModal - Modal wrapper for the new threaded chat system
 * Replaces the old Inbox component with MessagesLayout + MessagesProvider
 */

import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MessagesProvider } from "./MessagesContext";
import MessagesLayout from "./MessagesLayout";
import * as messagesApi from "../../lib/mudoNetwork/messagesApi";
import { useNotifications } from "../../contexts/NotificationsContext";

export default function InboxModal({ isOpen, onClose, initialThreadId = null }) {
  const navigate = useNavigate();
  const { offerNotifications = [] } = useNotifications() || {};

  // DEBUG: Test API directly
  useEffect(() => {
    if (isOpen) {
      messagesApi.getInboxMessages(10).then((result) => {
        console.log("InboxModal: Direct API test - inbox messages:", result);
      });
      messagesApi.getSentMessages(10).then((result) => {
        console.log("InboxModal: Direct API test - sent messages:", result);
      });
    }
  }, [isOpen]);
  
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div 
        className="relative z-10 w-full max-w-7xl h-[90vh] bg-black/40 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-4 bg-black/30 backdrop-blur-xl border-b border-white/10">
          <h2 className="text-2xl font-bold text-white">Messages</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors"
            aria-label="Close messages"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Messages Content */}
        <div className="pt-20 px-6 pb-6 h-full overflow-y-auto space-y-4">
          {Array.isArray(offerNotifications) && offerNotifications.length > 0 && (
            <section className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <div className="text-xs font-semibold text-neutral-300 mb-2 uppercase tracking-wide">
                Shop offers
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {offerNotifications.map((offer) => (
                  <button
                    key={offer.id}
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-xl bg-neutral-900/60 hover:bg-neutral-800/80 border border-white/5 text-sm transition-colors"
                    onClick={() => {
                      if (offer?.listingId) {
                        navigate(`/studio/marketplace/listings/${offer.listingId}`);
                      }
                      onClose?.();
                    }}
                  >
                    <div className="text-xs text-neutral-400">
                      New offer on {offer?.listingTitle || "listing"}
                    </div>
                    <div className="text-xs text-neutral-200">
                      {offer?.amount != null ? `$${offer.amount}` : ""}{" "}
                      from {offer?.buyer || "buyer"}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <MessagesProvider initialThreadId={initialThreadId}>
            <MessagesLayout />
          </MessagesProvider>
        </div>
      </div>
    </div>
  );
}
